import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Home, Building2, Users, Bed, Plus, Eye, Edit2 } from 'lucide-react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import EditPGModal from '@/components/EditPGModal';

export default function PGListPage() {
  const [pgs, setPgs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPG, setSelectedPG] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchPGs();
  }, []);

  useEffect(() => {
    if (user?.email === 'ichetanofficial@gmail.com' && pgs.length === 0) {
      handleSeedDemoData();
    }
  }, [user, pgs]);

  const fetchPGs = async () => {
    try {
      const response = await api.getPGs();
      const pgsData = response.data;
      setPgs(pgsData);
      
      // Fetch stats for each PG
      const statsPromises = pgsData.map(pg => api.getDashboard(pg.id));
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      pgsData.forEach((pg, idx) => {
        statsMap[pg.id] = statsResults[idx].data;
      });
      setStats(statsMap);
    } catch (error) {
      toast.error('Failed to fetch PGs');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDemoData = async () => {
    try {
      await api.seedDemoData();
      toast.success('Demo data loaded!');
      fetchPGs();
    } catch (error) {
      console.log('Demo data already exists or error:', error);
    }
  };

  const handleEditPG = (pg, e) => {
    e.stopPropagation();
    setSelectedPG(pg);
    setEditModalOpen(true);
  };

  const handleSavePG = async (formData) => {
    setSaveLoading(true);
    try {
      await api.updatePG(selectedPG.id, formData);
      toast.success('PG updated successfully!');
      setEditModalOpen(false);
      fetchPGs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PG');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (pgs.length === 0) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] px-4">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
            <Home className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">No PG Properties Yet</h2>
          <p className="text-slate-600 text-center mb-6 max-w-md">
            Get started by adding your first PG property
          </p>
          <Button
            onClick={() => navigate('/setup')}
            className="h-12 px-8 rounded-xl"
            data-testid="add-first-pg-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Your First PG
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading">Your PG Properties</h1>
            <p className="text-slate-600 mt-1">Manage all your paying guest properties</p>
          </div>
          <Button
            onClick={() => navigate('/setup')}
            className="h-12 px-6 rounded-xl"
            data-testid="add-pg-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add New PG
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pgs.map((pg) => {
            const pgStats = stats[pg.id] || {};
            const occupancyRate = pgStats.occupancy_rate || 0;
            const totalBeds = pgStats.total_beds || 0;
            const occupiedBeds = pgStats.occupied_beds || 0;
            const vacantBeds = pgStats.vacant_beds || 0;

            return (
              <div
                key={pg.id}
                onClick={() => navigate(`/pgs/${pg.id}/pending-rents`)}
                className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden hover:shadow-lg transition-all hover:border-primary cursor-pointer"
                data-testid={`pg-card-${pg.id}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{pg.name}</h3>
                        <p className="text-sm text-slate-500 line-clamp-1">{pg.address}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Bed className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-slate-700">Total Beds</span>
                      </div>
                      <span className="text-lg font-bold text-slate-900">{totalBeds}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs font-medium text-green-700 mb-1">Vacant</p>
                        <p className="text-xl font-bold text-green-600">{vacantBeds}</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-medium text-blue-700 mb-1">Occupied</p>
                        <p className="text-xl font-bold text-blue-600">{occupiedBeds}</p>
                      </div>
                    </div>

                    <div className="p-3 bg-primary/5 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-700">Occupancy</span>
                        <span className="text-lg font-bold text-primary">{occupancyRate}%</span>
                      </div>
                      <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${occupancyRate}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pg-layout/${pg.id}`);
                    }}
                    className="w-full h-11 rounded-xl"
                    data-testid={`view-layout-${pg.id}`}
                  >
                    <Eye className="w-5 h-5 mr-2" />
                    View Visual Layout
                  </Button>
                  
                  <Button
                    onClick={(e) => handleEditPG(pg, e)}
                    variant="outline"
                    className="w-full h-11 rounded-xl mt-2"
                    data-testid={`edit-pg-${pg.id}`}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit PG Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <EditPGModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        pg={selectedPG}
        onSave={handleSavePG}
        loading={saveLoading}
      />
    </Layout>
  );
}
